import {
	CalendarIcon,
	DashboardIcon,
	EnterIcon,
	IdCardIcon,
	PersonIcon,
} from '@radix-ui/react-icons'
import { type ReactNode } from 'react'
import { Link, useLocation } from 'react-router'

type DockUser = { username: string } | null | undefined

type DockItem = {
	key: string
	to: string
	label: string
	icon: ReactNode
	active: boolean
	/** the fly-to-cart animation lands here (see utils/cart-fly.tsx) */
	checkoutTarget?: boolean
}

/** Whole NOK, short form for the badge: "400 kr". */
const shortNOK = (nok: number) =>
	`${Math.round(nok).toLocaleString('nb-NO')} kr`

/**
 * The floating bottom bar: Program, the buyer's tickets (with the unpaid
 * total as a badge), the staff entrance (admin, or the door for door staff),
 * and the account. The lamp-yellow pill marks the current page and slides to
 * the next one on navigation (view transitions).
 */
export function SiteDock({
	user,
	staff,
	unpaidTotal,
}: {
	user: DockUser
	/** 'admin': the ticket list; 'door': ticket control (door staff) */
	staff: 'admin' | 'door' | null
	/** the buyer's unpaid total, shown on the tickets item */
	unpaidTotal: number | null
}) {
	const { pathname } = useLocation()
	const ticketsPath = user ? `/users/${user.username}/tickets` : null

	const items: DockItem[] = [
		{
			key: 'program',
			to: '/#program',
			label: 'Program',
			icon: <CalendarIcon />,
			active:
				pathname === '/' ||
				pathname.startsWith('/events') ||
				pathname.startsWith('/performers'),
		},
	]
	if (user && ticketsPath) {
		items.push({
			key: 'tickets',
			to: ticketsPath,
			label: 'Billettar',
			icon: <IdCardIcon />,
			active:
				pathname.startsWith(ticketsPath) ||
				pathname.startsWith('/tickets/checkout') ||
				pathname.startsWith('/tickets-receipt'),
			checkoutTarget: true,
		})
	}
	if (staff === 'door') {
		items.push({
			key: 'door',
			to: '/verification',
			label: 'Døra',
			icon: <EnterIcon />,
			active: pathname.startsWith('/verification'),
		})
	}
	if (staff === 'admin') {
		items.push({
			key: 'admin',
			to: '/tickets',
			label: 'Admin',
			icon: <DashboardIcon />,
			active:
				pathname === '/tickets' ||
				pathname.startsWith('/admin') ||
				pathname.startsWith('/statistics') ||
				pathname.startsWith('/verification'),
		})
	}
	items.push(
		user
			? {
					key: 'account',
					to: '/settings/profile',
					label: 'Konto',
					icon: <PersonIcon />,
					active: pathname.startsWith('/settings'),
				}
			: {
					key: 'login',
					to: '/login',
					label: 'Logg inn',
					icon: <PersonIcon />,
					active: ['/login', '/signup', '/verify', '/onboarding'].some((path) =>
						pathname.startsWith(path),
					),
				},
	)

	return (
		<nav className="site-dock" aria-label="Hovudmeny">
			{items.map((item) => (
				<Link
					key={item.key}
					to={item.to}
					viewTransition
					className="site-dock-item"
					aria-current={item.active ? 'page' : undefined}
					data-checkout-target={item.checkoutTarget ? '' : undefined}
				>
					<span className="site-dock-icon">
						{item.active ? <span className="site-dock-pill" /> : null}
						{item.icon}
					</span>
					<span className="site-dock-label">{item.label}</span>
					{item.checkoutTarget && unpaidTotal && unpaidTotal > 0 ? (
						<span
							key={unpaidTotal}
							data-checkout-badge
							className="site-dock-badge badge-pop"
						>
							{shortNOK(unpaidTotal)}
							<span className="sr-only"> ubetalt</span>
						</span>
					) : null}
				</Link>
			))}
		</nav>
	)
}
